import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { UnitService, Unit } from '../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../communication/project-comms.service';
import { ProjectFinancial } from '../../models/project/financial/financial.model';
import Utils from '../../util';
import { FinancialInvestment } from '../../models/project/financial/financial-investment/financial-investment.model';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { CommunicationService } from '../../communication/communication.service';
import { BehaviorSubject } from 'rxjs';
import { FinancialIncome } from '../../models/project/financial/financial-income.model';
import { ProjectService } from '../project.service';
import { FinancialCapex } from '../../models/project/financial/financial-capex.model';
import { FinancialOpex, FinancialOpexType } from '../../models/project/financial/financial-opex.model';
import { ConfiguredToastrService } from '../../helper/toastr/configured-toastr.service';
import { ProductFeeType, Product, ProductType } from '../../models/project/plant-specifications/product.model';
import { UnitValue } from '../../models/data-structures/unit-value.model';
import { UnitValueValidation } from '../../helper/validation/validation-sub-modules/unit-value.validation';
import { FeedstockService } from '../feedstock/feedstock.service';
import { FinancialFeeType } from '../../models/project/financial/financial-fee-type.enum';
import { Fuel, PeriodType } from '../../models/project/plant-specifications/fuel.model';
import { FeedstockValidation } from '../../helper/validation/validation-sub-modules/feedstock-validation/feedstock.validation';
import { FeedstockQuantitiesValidation } from '../../helper/validation/validation-sub-modules/feedstock-validation/feedstock-quantities.validation';
import { PlantSpecificationsValidation } from '../../helper/validation/validation-sub-modules/plant-spec-validation/plant-specifications.validation';
import { ProductValidation } from '../../helper/validation/validation-sub-modules/plant-spec-validation/product.validation';
import { FinancialProductValidation } from '../../helper/validation/validation-sub-modules/financial-validation/financial-product.validation';
import { FinancialFuelValidation } from '../../helper/validation/validation-sub-modules/financial-validation/financial-fuel.validation';
import { FuelValidation } from '../../helper/validation/validation-sub-modules/plant-spec-validation/fuel.validation';
import { ValidationService } from '../../helper/validation/validation.service';
import { AnessaTranslationService } from '../../helper/translation/anessa-translation.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectFinancialService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();
  financial = new ProjectFinancial();
  financialFullyLoadedNotifier = new BehaviorSubject<boolean>(false);
  financialLoadedNotifier = new BehaviorSubject<boolean>(false);
  financialUpdatedNotifier = new BehaviorSubject<boolean>(false);
  currencyUnitsNotifier = new BehaviorSubject<boolean>(false);
  currencyUnits: Unit[] = [];
  defaultItemsReadyNotifier = new BehaviorSubject<boolean>(false);
  private feedstockServiceLoaded = false;
  private projectServiceLoaded = false;
  private unitServiceReady = false;
  private digestateExpenseProductionAmountM3PerYear: {
    'whole digestate biofertilizer': number,
    'solid digestate biofertilizer': number,
    'thin digestate biofertilizer': number,
    'productRates',
    'consumedFuels'
  } = {
      'whole digestate biofertilizer': null,
      'solid digestate biofertilizer': null,
      'thin digestate biofertilizer': null,
      'productRates': null,
      'consumedFuels': null
    };

  constructor(
    private projectCommService: ProjectCommunicationService,
    private commService: CommunicationService,
    private projectService: ProjectService,
    private unitService: UnitService,
    private feedstockService: FeedstockService,
    private toastrService: ConfiguredToastrService,
    private validationService: ValidationService,
    public translationService: AnessaTranslationService
  ) {
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.projectServiceLoaded = loaded;
        this.setup();
      });
    this.feedstockService.feedstockServiceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.feedstockServiceLoaded = loaded;
        this.setup();
      });
    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.financial = this.projectCommService.getFinancial();
          this.financialUpdatedNotifier.next(true);
        }
      });
    this.projectCommService.projectLoadedChange
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(changed => {
        if (changed) {
          this.financial = this.projectCommService.getFinancial();
          this.financialUpdatedNotifier.next(true);
        }
      });
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((ready: boolean) => {
        this.unitServiceReady = ready;
        if (ready) {
          this.setup();
        }
      });
  }

  setup() {
    if (this.projectServiceLoaded && this.feedstockServiceLoaded && this.unitServiceReady) {
      this.financial = this.projectCommService.getFinancial();
      if (!this.currencyUnitsNotifier.value) {
        this.fetchExchangeRateData();
      }
      this.financialLoadedNotifier.next(true);
    } else {
      this.financial = null;
      this.financialLoadedNotifier.next(false);
    }
    this.updateFinancialFullyLoaded();
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  updateFinancialFullyLoaded() {
    this.financialFullyLoadedNotifier.next(
      this.financialLoadedNotifier.value
      && this.currencyUnitsNotifier.value
      && this.feedstockServiceLoaded
      && this.unitServiceReady
      && this.projectServiceLoaded
    );
  }

  getCurrencyUnitById(id: string): Unit {
    if (this.currencyUnitsNotifier.value) {
      return this.currencyUnits.find(unit => unit.id === id);
    } else {
      console.error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.UNITS_NL'));
    }
  }

  getProjectPrecision(): number {
    return this.projectCommService.getPrecision();
  }

  getIncomeArray() {
    if (this.financial && this.financial.income) {
      return Utils.deepCopy(this.financial.income);
    }
  }

  getIncomeIndexByName(name: string) {
    if (this.financial) {
      const income = this.financial.income;
      if (income && income.length > 0) {
        for (let i = 0; i < income.length; i++) {
          if (income[i].name === name) {
            return i;
          }
        }
      }
    }
    throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.INCOME_INDEX_ERROR'));
  }

  getIncome(index: number) {
    if (this.isValidIncomeIndex(index)) {
      return FinancialIncome.deepCopy(this.financial.income[index]);
    }
  }

  addIncome(income: FinancialIncome) {
    if (this.financial && this.financial.income) {
      if (Utils.nameIsUnique(income.name, this.financial.income)) {
        this.financial.income.push(income);
        this.save();
        return true;
      } else {
        this.toastrService.error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.INCOME_EXISTS_ERROR'), this.translationService.get('SERVICE.PROJECT_FINANCIAL.INCOME_SAVE_ERROR'));
      }
    }
    return false;
  }

  saveIncome(income: FinancialIncome, index: number) {
    if (this.isValidIncomeIndex(index)) {
      if (Utils.nameIsUnique(income.name, this.financial.income, index)) {
        this.financial.income[index] = income;
        this.save();
        return true;
      } else {
        this.toastrService.error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.INCOME_EXISTS_ERROR'), this.translationService.get('SERVICE.PROJECT_FINANCIAL.INCOME_SAVE_ERROR'));
      }
    }
    return false;
  }

  removeIncome(index: number) {
    if (this.isValidIncomeIndex(index)) {
      this.financial.income.splice(index, 1);
      this.save();
    }
  }

  clearAllIncome() {
    this.financial.income = [];
    this.save();
  }

  getCapex(): FinancialCapex[] {
    if (this.financial && this.financial.capex) {
      let capex = [];
      this.financial.capex.forEach(c => capex.push(FinancialCapex.deepCopy(c)));
      return capex;
    }
  }

  getCapexIndexByName(name: string) {
    if (this.financial) {
      const capex = this.financial.capex;
      if (capex && capex.length > 0) {
        for (let i = 0; i < capex.length; i++) {
          if (capex[i].name === name) {
            return i;
          }
        }
      }
    }
    throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.OPEX_INDEX_ERROR'));
  }

  saveCapex(capex: FinancialCapex[]) {
    if (this.financial) {
      this.financial.capex = capex;
      this.save();
    }
  }

  getOpex() {
    if (this.financial && this.financial.opex) {
      return FinancialOpex.arrayDeepCopy(this.financial.opex);
    }
  }

  getOpexIndexByName(name: string) {
    if (this.financial) {
      const opex = this.financial.opex;
      if (opex && opex.length > 0) {
        for (let i = 0; i < opex.length; i++) {
          if (opex[i].name === name) {
            return i;
          }
        }
      }
    }
    throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.OPEX_INDEX_ERROR'));
  }

  saveOpex(opex: FinancialOpex[]) {
    if (this.financial) {
      this.financial.opex = opex;
      this.save();
    }
  }

  getFinancialInvestment() {
    return FinancialInvestment.deepCopy(this.financial.investment);
  }

  saveFinancialInvestment(finInv: FinancialInvestment) {
    this.financial.investment = finInv;
    this.save();
  }

  getTotalOfIncomeInCad() {
    const totalInCad = new UnitValue({ value: 0, unit: this.getUsdUnitId() });
    if (this.financial && this.financial.income) {
      for (const income of this.financial.income) {
        if (income.income && new UnitValueValidation(income.income).isValid()) {
          const incomeUnit = this.getCurrencyUnitById(income.income.unit);
          const incomeInCad = incomeUnit.convertToBase(income.income.value);
          totalInCad.value += incomeInCad;
        }
      }
    }
    return totalInCad;
  }

  getTotalIncomeInTargetCur() {
    const totalInTargetCur = new UnitValue();
    const totalInCad = this.getTotalOfIncomeInCad();
    let targetCurUnit: Unit;
    targetCurUnit = this.getCurrencyUnitById(this.getTotalIncomeTargetUnit());
    totalInTargetCur.unit = targetCurUnit.id;
    totalInTargetCur.value = targetCurUnit.convertFromBase(totalInCad.value);
    return totalInTargetCur;
  }

  getUsdUnitId() {
    if (this.currencyUnits) {
      const foundUnit = this.currencyUnits.find(unit => unit.displayName === 'USD');
      if (foundUnit) {
        return foundUnit.id;
      }
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.NO_USD'));
    }
  }

  getCadUnitId() {
    if (this.currencyUnits) {
      const foundUnit = this.currencyUnits.find(unit => unit.displayName === 'CAD');
      if (foundUnit) {
        return foundUnit.id;
      }
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.NO_CAD'));
    }
  }

  updateTotalIncomeSaveUnit(unitId: string) {
    if (this.financial && this.financial.savedTotalIncomeUnit !== unitId) {
      this.financial.savedTotalIncomeUnit = unitId;
      this.save();
    }
  }

  getSavedTotalCapexUnit() {
    if (this.financial) {
      return this.financial.savedTotalCapexUnit;
    }
  }

  updateSavedTotalCapexUnit(savedTotalCapexUnit: string) {
    if (this.financial) {
      this.financial.savedTotalCapexUnit = savedTotalCapexUnit;
    }
  }

  getSavedTotalOpexUnit() {
    if (this.financial) {
      return this.financial.savedTotalOpexUnit;
    }
  }

  updateSaveTotalOpexUnit(savedTotalOpexUnit: string) {
    if (this.financial) {
      this.financial.savedTotalOpexUnit = savedTotalOpexUnit;
    }
  }

  /**
   * The below function returns the default lifetime parameter
   * which can be set inside `investment=>capital expenditure`.
   * This value can be different from what is used in loan computations in java backend.
   * For that please take a look at `getLongestCapexLifetime` function.
   */
  getDefaultLifetime() {
    const fallbackLifetime = new UnitValue();
    fallbackLifetime.value = 20;
    fallbackLifetime.unit = this.unitService.getUnitByName('years').id;
    if (this.financial && this.financial.savedDefaultLifetime) {
      if (!Utils.isNumeric(this.financial.savedDefaultLifetime.value) || this.financial.savedDefaultLifetime.value < 1) {
        this.financial.savedDefaultLifetime = fallbackLifetime;
      }
      return UnitValue.deepCopy(this.financial.savedDefaultLifetime);
    }
    return UnitValue.deepCopy(fallbackLifetime);
  }

  /**
   * There is a default lifetime parameter, which can be in `investment=>capital expenditure`.
   * There are also lifetime parameters inside each capex item.
   * The `getDefaultLifetime` function returns the default lifetime value.
   * The below functions returns the longest lifetime in capex items + 1.
   * If there are no capex items, it will return `getDefaultLifetime()`.
   */
  getLongestCapexLifetime() {
    let lifetime = -1;
    if (this.financial && this.financial.capex) {
      for (const item of this.financial.capex) {
        if (item.lifetime && item.lifetime.value) {
          if (item.lifetime.value >= lifetime) {
            lifetime = item.lifetime.value;
          }
        }
      }
    }
    if (lifetime < 0) return this.getDefaultLifetime();
    const lifetimeUV = new UnitValue();
    // In the java side,t
    lifetimeUV.value = lifetime + 1;
    lifetimeUV.unit = this.unitService.getUnitByName('years').id;
    return lifetimeUV;
  }

  updatedDefaultLifetime(updatedLifetime: UnitValue) {
    if (this.financial) {
      this.financial.savedDefaultLifetime = new UnitValue(updatedLifetime);
    }
  }

  checkForBiomethOrElec() {
    const bioProcess = this.projectCommService.getProjectPlantSpecifications();
    if (bioProcess && bioProcess.generatedProducts) {
      if (bioProcess.generatedProducts.length > 0) {
        for (const genProd of bioProcess.generatedProducts) {
          if (genProd.type === ProductType.Electric || genProd.type === ProductType.Biometh) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Get the total cost for all entire list of capex items provided as a parameter.
   * @param capex is a list of capex items that will have its total added up and returned.
   */
  getTotalCapexAmountInCad(capex?: FinancialCapex[]): UnitValue {
    if (!capex) {
      capex = this.getCapex();
    }
    if (!capex) throw Error('failed to calculate total capex, capex not found');
    const totalInCad = new UnitValue({
      value: 0,
      unit: this.getCadUnitId()
    });
    for (const item of capex) {
      if (item && !item.connectionToggle && new UnitValueValidation(item.cost).isValid()) {
        const incomeUnit = this.getCurrencyUnitById(item.cost.unit);
        let incomeInCad = incomeUnit.convertToBase(item.cost.value);
        totalInCad.value += incomeInCad;
      } else if (item && item.connectionToggle && new UnitValueValidation(item.cost).isValid() && new UnitValueValidation(item.quantity).isValid() && new UnitValueValidation(item.period).isValid()) {
        const incomeUnit = this.getCurrencyUnitById(item.cost.unit);
        let rate = 0;
        this.getRates() !== null ? rate = this.getRates()[item.connectionType] : this.incompleteConnection();
        let incomeInCad = incomeUnit.convertToBase(item.cost.value) * rate * this.unitService.getValueInBaseUnit(item.period) / this.unitService.getValueInBaseUnit(item.quantity);
        totalInCad.value += incomeInCad;
      }
    }
    return totalInCad;
  }

  /**
   * Checks if the project has all the inputs needed for the tech results to be computed so the default digestate
   * expense opex item can be calculated. This function triggers the defaultItemsReadyNotifier when the
   * value needed from the tech results is fetched, unless the project isn't valid. In which case the notifier will
   * be triggered.
   *
   * THIS MUST BE RAN BEFORE TRYING TO WORK WITH DEFAULT OPEX ITEMS.
   */
  initializeDefaultItemCalculation() {
    // Check if a project is loaded to prevent error when on project input page
    const projectValidation = this.validationService.getProjectValidation();
    const isProjectInput = this.projectCommService.isProjectInput();
    if (projectValidation.plantSpecificationsPercentage === 100
      && projectValidation.feedstockPercentage === 100
      && Utils.isProjectLoaded()
      && !isProjectInput
    ) {
      this.commService.fetchDigestateM3ProductionPerYear()
        .pipe(take(1)).subscribe(response => {
          if (response) {
            this.digestateExpenseProductionAmountM3PerYear = response;
            this.defaultItemsReadyNotifier.next(true);
          } else {
            this.defaultItemsReadyNotifier.next(false);
            throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.FAILED_FETCH'));
          }
        });
    } else {
      this.defaultItemsReadyNotifier.next(true);
    }
  }

  /**
   * Get calculated opex items for the current project.
   */
  getDefaultItemsForOpex() {
    const defaultItems: FinancialOpex[] = [];
    defaultItems.push(this.getFeedstockExpenseDefaultItem());
    defaultItems.push(this.getDigestateExpenseDefaultItem());
    defaultItems.push(this.getConsumedFuelDefaultItem());
    return defaultItems;
  }

  /**
   * Get a financial opex item for the feedstock expenses.
   */
  getFeedstockExpenseDefaultItem(): FinancialOpex {
    if (!this.feedstockServiceLoaded) {
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.FEED_SERVICE_NL'));
    }
    const feedstockExpense = new FinancialOpex();
    const cadUnitId = '000000000000000000002004';
    const yearUnitId = '000000000000000000001305';
    feedstockExpense.type = FinancialOpexType.FEEDSTOCK;
    feedstockExpense.cost = new UnitValue({ value: this.getTotalYearlyFeedstockCostInCad(), unit: cadUnitId });
    feedstockExpense.name = 'SERVICE.PROJECT_FINANCIAL.FE';
    feedstockExpense.period = new UnitValue({ value: 1, unit: yearUnitId });
    return feedstockExpense;
  }

  /**
   * Get a value for the total cost, in CAD, of all feedstocks for the current project.
   */
  getTotalYearlyFeedstockCostInCad(): number {
    if (!this.feedstockServiceLoaded) {
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.FEED_SERVICE_NL'));
    }
    let totalCostPerYear = 0;
    const feedstocks = this.feedstockService.getFeedstocks();
    // for each feedstock stock
    if (feedstocks && feedstocks.length > 0) {
      for (const feedstock of feedstocks) {
        if (feedstock.financial) {
          let price = 0;
          let transportPrice = 0;
          const validation = new FeedstockValidation(feedstock);
          // if has inputs needed for mass calculation
          if (new FeedstockQuantitiesValidation(feedstock.quantities).isValid()) {
            const amountInTonPerYear = this.feedstockService.getNormalizedMassPerYear(feedstock);
            // if has inputs for base (non transport) calculations
            if (validation.isValidForOpexExpenseNonTransport()) {
              // if is cost
              if (feedstock.financial.feeType === FinancialFeeType.COST) {
                // updated non transport cost
                const priceInSelectedUnit = this.feedstockService.getNormalizedPricePerTonValue(feedstock);
                const selectedUnit = this.currencyUnits.find(x => x.id === feedstock.financial.price.unit);
                price = selectedUnit.convertToBase(priceInSelectedUnit) * amountInTonPerYear;
              }
            }
            // if transport cost is being used
            if (feedstock.financial.useTransport) {
              // if all inputs are valid
              if (validation.isValidForOpexExpenseTransport()) {
                // update transport cost
                const transportPriceInSelectedUnit = this.feedstockService.getNormalizedTransportPricePerTonValue(feedstock);
                const selectedTransportUnit = this.currencyUnits.find(
                  x => x.id === feedstock.financial.transportPrice.unit);
                transportPrice = selectedTransportUnit.convertToBase(transportPriceInSelectedUnit) * amountInTonPerYear;
              }
            }
          }
          totalCostPerYear += price;
          totalCostPerYear += transportPrice;
        }
      }
    }
    return totalCostPerYear;
  }

  /**
   * Get a financial opex item for the digestate expenses.
   */
  getDigestateExpenseDefaultItem(): FinancialOpex {
    if (!this.feedstockServiceLoaded) {
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.FEED_SERVICE_NL'));
    }
    const digestateExpense = new FinancialOpex();
    const cadUnitId = '000000000000000000002004';
    const yearUnitId = '000000000000000000001305';
    digestateExpense.type = FinancialOpexType.DIGESTATE;
    digestateExpense.cost = new UnitValue({ value: this.getTotalYearlyDigestateCostInCad(), unit: cadUnitId });
    digestateExpense.name = 'SERVICE.PROJECT_FINANCIAL.DE';
    digestateExpense.period = new UnitValue({ value: 1, unit: yearUnitId });
    return digestateExpense;
  }

  /**
   * Get a value for the total cost, in CAD, of all feedstocks for the current project.
   */
  getTotalYearlyDigestateCostInCad(): number {
    if (!this.projectServiceLoaded) {
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.PROJECT_SERVICE_NL'));
    }
    let totalCostPerYear = 0;
    const products: Product[] = this.projectCommService.getGeneratedProductItems();
    const plantSpec = this.projectCommService.getProjectPlantSpecifications();
    const validation = new PlantSpecificationsValidation(plantSpec);
    // if valid
    if (validation.isValid()) {
      const hasDewatering: boolean = plantSpec.hasDewatering();

      // if has dewatering then thin digestate
      if (hasDewatering) {
        const thinDigestate = products.find(x => x.type === ProductType.ThinDigestate);
        if (thinDigestate) {
          const pricePerM3InCad = this.getDigestateCostPerM3InCad(thinDigestate);
          let production = 0;
          if (this.digestateExpenseProductionAmountM3PerYear) {
            production = this.digestateExpenseProductionAmountM3PerYear['thin digestate biofertilizer'];
          }
          totalCostPerYear = production * pricePerM3InCad;
        }
        // solid digestate
        const solidDigestate = products.find(x => x.type === ProductType.SolidDigestate);
        if (solidDigestate) {
          const pricePerM3InCad = this.getDigestateCostPerM3InCad(solidDigestate);
          let production = 0;
          if (this.digestateExpenseProductionAmountM3PerYear) {
            production = this.digestateExpenseProductionAmountM3PerYear['solid digestate biofertilizer'];
          }
          totalCostPerYear += production * pricePerM3InCad;
        }
      } else if (plantSpec.isDryBatch()) {
        const solidDigestate = products.find(x => x.type === ProductType.SolidDigestate);
        if (solidDigestate) {
          const pricePerM3InCad = this.getDigestateCostPerM3InCad(solidDigestate);
          let production = 0;
          if (this.digestateExpenseProductionAmountM3PerYear) {
            production = this.digestateExpenseProductionAmountM3PerYear['solid digestate biofertilizer'];
          }
          totalCostPerYear = production * pricePerM3InCad;
        }
        // else whole digestate
      } else {
        const wholeDigestate = products.find(x => x.type === ProductType.WholeDigestate);
        if (wholeDigestate) {
          const pricePerM3InCad = this.getDigestateCostPerM3InCad(wholeDigestate);
          let production = 0;
          if (this.digestateExpenseProductionAmountM3PerYear) {
            production = this.digestateExpenseProductionAmountM3PerYear['whole digestate biofertilizer'];
          }
          totalCostPerYear = production * pricePerM3InCad;
        }
      }
    }
    if (totalCostPerYear < 0) {
      totalCostPerYear = 0;
    }
    return totalCostPerYear;
  }

  /**
   * Get the cost of a sellable digestate product in CAD.
   * @param product is a product that must be a sellable digestate products.
   */
  getDigestateCostPerM3InCad(product: Product): number {
    if (!new ProductValidation(product).isValid() ||
      !new FinancialProductValidation(product).isValid() ||
      product.priceType === ProductFeeType.FREE ||
      product.priceType === ProductFeeType.INCOME
    ) {
      return 0;
    }
    if (product && product.isSellableDigestate()) {
      if (product.priceType === ProductFeeType.FREE) {
        return 0;
      }
      const price = product.price;
      const amount = product.quantity;

      const selectedAmountUnit = this.unitService.getUnitById(amount.unit);
      const amountInKgOrL = selectedAmountUnit.convertToBase(amount.value);

      const selectedCurrencyUnit = this.currencyUnits.find(x => x.id === price.unit);
      const priceInCad = selectedCurrencyUnit.convertToBase(price.value);

      const m3Unit = this.unitService.getUnitById('000000000000000000001103');

      return priceInCad / m3Unit.convertFromBase(amountInKgOrL);
    }
    throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.INVALID_PRODUCT'));
  }

  /**
   * Get a financial opex item for the consumed fuel / utilities expense.
   */
  private getConsumedFuelDefaultItem(): FinancialOpex {
    if (!this.projectServiceLoaded) {
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.PROJECT_SERVICE_NL'));
    }
    const utilitiesExpenseItem = new FinancialOpex();
    const cadUnitId = '000000000000000000002004';
    const yearUnitId = '000000000000000000001305';
    utilitiesExpenseItem.type = FinancialOpexType.CONSUMABLES;
    utilitiesExpenseItem.cost = new UnitValue({ value: this.getTotalYearlyConsumedFuelCostInCad(), unit: cadUnitId });
    utilitiesExpenseItem.name = 'SERVICE.PROJECT_FINANCIAL.CFUE';
    utilitiesExpenseItem.period = new UnitValue({ value: 1, unit: yearUnitId });

    return utilitiesExpenseItem;
  }

  /**
   * Get a value for the total cost, in CAD, of all consumed fuels for the current project.
   */
  private getTotalYearlyConsumedFuelCostInCad(): number {
    if (!this.projectServiceLoaded) {
      throw Error(this.translationService.get('SERVICE.PROJECT_FINANCIAL.PROJECT_SERVICE_NL'));
    }
    let totalCostPerYear = 0;
    const fuels: Fuel[] = this.projectCommService.getConsumedFuelItems();
    if (fuels && fuels.length > 0) {
      // for all fuels
      for (const fuel of fuels) {
        // if fuel is valid
        if (new FuelValidation(fuel).isValid() && new FinancialFuelValidation(fuel).isValid()) {
          // calculate cost per year in CAD and add to running total
          const priceInBaseUnit = this.getFuelCostPerBaseUnitInCad(fuel);
          const amountInBaseUnitPerYear = this.getConsumedFuelAmountPerYear(fuel);
          totalCostPerYear += priceInBaseUnit * amountInBaseUnitPerYear;
        }
      }
    }
    return totalCostPerYear;
  }

  /**
   * Get the cost per a fuels base unit in CAD.
   * @param fuel is a Fuel object.
   */
  private getFuelCostPerBaseUnitInCad(fuel: Fuel): number {
    if (fuel && fuel.cost && fuel.amount) {
      const cost = fuel.cost;
      const amount = fuel.amount;

      const selectedCurrencyUnit = this.currencyUnits.find(x => x.id === cost.unit);
      const costInCad = selectedCurrencyUnit.convertToBase(cost.value);

      const amountInBase = this.unitService.getValueInBaseUnit(amount);

      return costInCad / amountInBase;
    }
    return 0;
  }

  /**
   * Get the amount of fuel per year.
   * @param fuel is a Fuel object.
   */
  private getConsumedFuelAmountPerYear(fuel: Fuel): number {
    if (fuel && fuel.quantity && fuel.period) {
      if (fuel.perType === PeriodType.PERIOD) {
        const selectedUnit = this.unitService.getUnitById(fuel.quantity.unit);
        const quantInBase = selectedUnit.convertToBase(fuel.quantity.value);

        const selectedPeriodUnit = this.unitService.getUnitById(fuel.period.unit);
        const days = selectedPeriodUnit.convertToBase(fuel.period.value);

        return quantInBase / (days / 365);
      } else {
        if (this.digestateExpenseProductionAmountM3PerYear && this.digestateExpenseProductionAmountM3PerYear['consumedFuels'] && this.digestateExpenseProductionAmountM3PerYear['consumedFuels'][fuel.name]) {
          const consumedPerDay = this.digestateExpenseProductionAmountM3PerYear['consumedFuels'][fuel.name];
          const consumedPerYear = consumedPerDay * 365;

          return consumedPerYear;
        }

      }
    }
    return 0;
  }

  private isValidIncomeIndex(index: number) {
    if (this.financial && this.financial.income) {
      if (index > -1 && index < this.financial.income.length) {
        return true;
      }
    }
    return false;
  }

  private save() {
    this.projectCommService.saveFinancial(this.financial);

  }

  private getTotalIncomeTargetUnit() {
    let targetUnit = '';
    targetUnit = this.financial.savedTotalIncomeUnit;
    if (!targetUnit) { // if saved is null try this
      targetUnit = this.projectService.getDefaultCurrency();
    }
    if (!targetUnit) {// if default is null try this
      targetUnit = this.getFirstIncomeUnit();
    }
    if (!targetUnit) { // if no first income unit for some reason, try this
      targetUnit = this.getUsdUnitId(); // USD
    }
    return targetUnit;
  }

  private fetchExchangeRateData() {
    this.unitService.getCurrencyUnits().then(
      (units: Unit[]) => {
        const curUnits = units;

        this.currencyUnits = this.sortUnitsByOrder(curUnits);
        this.currencyUnitsNotifier.next(true);
        this.updateFinancialFullyLoaded();
      }
    );
  }

  private sortUnitsByOrder(units: Unit[]): Unit[] {
    if (units) {
      units.sort((first: Unit, second: Unit) => {
        if (Number(first.order) < Number(second.order)) {
          return -1;
        } else if (Number(second.order) < Number(first.order)) {
          return 1;
        } else {
          return 0;
        }
      });
    }
    return units;
  }

  private getFirstIncomeUnit() {
    if (this.financial && this.financial.income) {
      if (this.financial.income.length > 0) {
        const firstIncome = this.financial.income[0];
        if (firstIncome.income && firstIncome.income.unit) {
          return firstIncome.income.unit;
        }
      }
    }
  }

  public getRates() {
    if (this.digestateExpenseProductionAmountM3PerYear) {
      return this.digestateExpenseProductionAmountM3PerYear['productRates'];
    }
    return null;
  }

  public setResultProductRates(rates) {
    this.digestateExpenseProductionAmountM3PerYear['productRates'] = rates;
  }

  public setResultFuelRates(rates) {
    this.digestateExpenseProductionAmountM3PerYear['consumedFuels'] = rates;
  }

  connectionToastr;
  public incompleteConnection() {
    if (this.connectionToastr && this.toastrService.toasts().find(toast => toast.toastId === this.connectionToastr)) { return; }
    const projectValidation = this.validationService.getProjectValidation();
    if (!this.digestateExpenseProductionAmountM3PerYear && (projectValidation.plantSpecificationsPercentage !== 100 || projectValidation.feedstockPercentage !== 100)) {
      let warning = this.translationService.get('SERVICE.PROJECT_FINANCIAL.INVALID_CONNECTIONS');
      this.connectionToastr = this.toastrService.warning(warning, '', 0).toastId;
    }
  }
}

